import {
  GoogleAuthProvider,
  fetchSignInMethodsForEmail,
  signInWithCredential
} from 'firebase/auth';
import auth from '@/lib/firebase/client';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { deleteCookie, setCookies } from '@/actions/auth';
import { getAppStatus, updateLoginStats } from '@/actions/user';
import { getRedirectPathByAppStatus } from '@/utils/appStatus';
import { createUser, getUsersExists } from '../actions';
import { APP_STATUS } from '@/constants/appStatus';
import { sendEvent } from '@/lib/firebase/analytics/sendEvent';
import { EVENTS } from '@/lib/firebase/analytics/events';
import { PATH_AFTER_ACCOUNT_REGISTRATION } from '../constants';

export const useGoogleAuth = (
  setIsLoading: Dispatch<SetStateAction<boolean>>
) => {
  const router = useRouter();
  const currentPathname = usePathname();

  // NOTE: Googleの認証画面で認証が完了すると、URLのフラグメント識別子(#)にGoogleのトークンが付与されてリダイレクトされるので、
  //       そのトークンがあれば、firebase authに登録もしくはログインを行う処理を実行する
  useEffect(() => {
    (async () => {
      // NOTE: フラグメント識別子(#)でデータが付与されてくるのでクエリパラメータ(?)に変更する
      const { searchParams } = new URL(location.href.replace('#', '?'));
      const googleAccessToken = searchParams.get('access_token');
      if (!googleAccessToken) return;

      setIsLoading(true);
      const { isSuccess, message, redirectPath } =
        await authenticateFirebaseWithGoogle(googleAccessToken);

      if (isSuccess) {
        location.href = redirectPath;
        return;
      }

      // NOTE: ログインに失敗したときにパラメータのトークン情報等を消したいため
      router.replace(redirectPath);
      setIsLoading(false);
      // eslint-disable-next-line no-alert
      alert(message);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // NOTE: Googleログインボタン押下後、Googleの認証画面にリダイレクト
  const redirectGoogleAuthPage = async () => {
    setIsLoading(true);
    const { origin, pathname } = location;
    const endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';
    const options = {
      client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
      redirect_uri: origin + pathname,
      response_type: 'token',
      scope: 'https://www.googleapis.com/auth/userinfo.email',
      prompt: 'select_account'
    } as const;

    const queryParams = new URLSearchParams(options).toString();
    location.href = `${endpoint}?${queryParams}`;
  };

  // NOTE: firebase authにGoogleのトークンを使って登録もしくはログインを行う処理
  const authenticateFirebaseWithGoogle = async (googleAccessToken: string) => {
    try {
      await deleteCookie('guestUserToken');

      const googleUserInfoRes = await fetch(
        'https://www.googleapis.com/oauth2/v1/userinfo',
        {
          headers: {
            Authorization: `Bearer ${googleAccessToken}`
          }
        }
      );

      if (!googleUserInfoRes.ok) {
        throw new Error(googleUserInfoRes.statusText);
      }
      const googleUserInfo = await googleUserInfoRes.json();

      // NOTE: google.comやpasswordなどの配列が返ってくる
      const signInMethods = await fetchSignInMethodsForEmail(
        auth,
        googleUserInfo.email
      );

      // NOTE: すでに別の認証方法で登録されている場合
      if (signInMethods.length && !signInMethods.includes('google.com')) {
        return {
          isSuccess: false,
          message:
            'このメールアドレスは別の認証方法で登録されています。別の認証方法で再度お試しください。',
          redirectPath: currentPathname
        } as const;
      }

      const oauthCredential = GoogleAuthProvider.credential(
        null,
        googleAccessToken
      );

      // NOTE: アカウントがなければ登録、あればログインをよしなにやってくれる
      const { user } = await signInWithCredential(auth, oauthCredential);
      if (!user.email) {
        throw new Error('Email is null');
      }

      // NOTE: Firebase Authenticationのアクセストークンの有効期限は１時間
      //       バグ防止で実際の有効期限より10分短くする
      const fiftyMinutesMilliseconds = 60 * 50 * 1000;
      const fiftyMinutesLater = Date.now() + fiftyMinutesMilliseconds;

      await setCookies({
        accessToken: await user.getIdToken(),
        refreshToken: user.refreshToken,
        userId: user.uid,
        accessTokenExpiration: String(fiftyMinutesLater)
      });

      const userExists = await getUsersExists();

      // ビルド時に設定されるバージョンの環境変数
      const appVersion = process.env.NPM_PACKAGE_VERSION || '';

      if (!userExists) {
        const createUserResult = await createUser(
          {
            mailAddress: user.email,
            fcmToken: '',
            appStatus: APP_STATUS.accountCreated,
            webAppVersion: appVersion
          },
          await user.getIdToken()
        );

        if (!createUserResult.isSuccess) {
          throw Error(createUserResult.errorMessage);
        }

        await Promise.all([
          updateLoginStats(),
          setCookies({
            appStatus: APP_STATUS.accountCreated
          }),
          // Firebaseイベント発火
          sendEvent(EVENTS.user_account_create, {
            userId: user.uid
          })
        ]);

        return {
          isSuccess: true,
          message: '',
          redirectPath: PATH_AFTER_ACCOUNT_REGISTRATION
        } as const;
      } else {
        // NOTE: エンジニア版に登録済の場合
        const appStatus = await getAppStatus();
        if (!appStatus) throw new Error('AppStatus is null');

        await Promise.all([
          updateLoginStats(),
          setCookies({
            appStatus
          }),
          // Firebaseイベント発火
          sendEvent(EVENTS.log_in, { userId: user.uid })
        ]);

        const redirectPath = await getRedirectPathByAppStatus(appStatus);

        return {
          isSuccess: true,
          message: '',
          redirectPath
        } as const;
      }
    } catch (e) {
      return {
        isSuccess: false,
        message: 'エラーが発生しました。時間を置いてから再度お試しください。',
        redirectPath: currentPathname
      } as const;
    }
  };

  return {
    redirectGoogleAuthPage
  };
};
